import {useContext} from "react";
import {UnitContext} from "../app/App";
import {StorageKey_JWT} from "@intuitionrobotics/user-account/frontend";
import {PermissionLevels, PermissionUnitsAccess} from "@app-sp/app-shared/shared";
import { UserGroupTypes } from "@app/ir-q-app-common/types/units";

type JWTTokenInformation = {
    exp: number,
    iat: number,
    iss: string,
    permissionLevels: PermissionLevels,
    account: {
        email: string,
        _id: string,
    }
}

export function useUnitExists() {
    const {activatedUnits} = useContext(UnitContext);
    return {
        unitExists: (unitId: string) => activatedUnits.has(unitId)
    }
}

// Throw-safe: a missing/malformed token returns undefined instead of throwing.
// This is called synchronously from render paths (registerRoutes ->
// getTabPermissions, permission checks on unit pages); on tab-reactivation the
// JWT can be briefly absent while the session revalidates, and a throw here
// would blank the entire app.
export const decodeJWT = (token?: string): JWTTokenInformation | undefined => {
    if (!token)
        return undefined;

    try {
        const base64Url = token.split('.')[1];
        if (!base64Url)
            return undefined;

        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload) as JWTTokenInformation;
    } catch {
        return undefined;
    }
}

export const getUserTokenInfo = (): JWTTokenInformation | undefined => {
    const jwt = StorageKey_JWT.get();
    return decodeJWT(jwt);
};

export const extractPermissions: (userInformation?: JWTTokenInformation) => PermissionLevels | undefined = (userInformation?: JWTTokenInformation) => userInformation?.permissionLevels;

export const getTabPermissions = () => {
    const userInformation = getUserTokenInfo();
    return extractPermissions(userInformation)?.tabs || [];
}

export const getUnitsPermissions: () => PermissionUnitsAccess | undefined = () => {
    const userInformation = getUserTokenInfo();
    return extractPermissions(userInformation)?.units;
}

export const hasWritePermissionForUnit = (unitId: string, userGroup?: string, userGroupType?: UserGroupTypes) => {
    const unitPermissions = getUnitsPermissions();
    if (!unitPermissions)
        return false;

    const {unitIds, userGroups, userGroupTypes} = unitPermissions.write;
    if (unitIds?.includes(".*") || unitIds?.includes(unitId))
        return true;

    if (userGroup && userGroups?.includes(userGroup))
        return true;

    if (userGroupType && userGroupTypes?.includes(userGroupType))
        return true;

    return false
}